// Konta, sesje, role i macierz dostępu — mirrors backend/app/auth_contract.py.
// Sesja siedzi w ciasteczku HttpOnly (front go nie widzi); fetch na ten sam
// origin wysyła je sam.
//
// Role żyją w bazie (migracja 060) — klucz roli to zwykły string, a nazwy idą
// z API (/api/admin/roles dla admina, `me.role_labels` dla własnego konta).
// Jedyna rola znana z góry to systemowy admin.

import { useCallback, useEffect, useState } from 'react'

export type Role = string
export const ADMIN_ROLE: Role = 'admin'

/** Podgląd ⊂ Edycja. Sekcji bez dostępu nie ma w `Me.access` ani w macierzy. */
export type Level = 'view' | 'edit'
export const LEVELS: Level[] = ['view', 'edit']
export const LEVEL_LABEL: Record<Level, string> = { view: 'Podgląd', edit: 'Edycja' }

/** invited = konto bez hasła (czeka na link z zaproszenia), disabled = nieaktywne. */
export type UserStatus = 'invited' | 'active' | 'disabled'

/** Ta sama granica co PASSWORD_MIN_LEN w backend/app/auth.py — na froncie tylko
 *  podpowiedź i blokada przycisku; rozstrzyga backend. */
export const PASSWORD_MIN_LEN = 10

export interface AppUser {
  id: string
  // Nowe konta (zaproszenia): login = e-mail. Stare: login bez „@", e-mail opcjonalny.
  login: string
  email: string | null
  display_name: string
  roles: Role[]
  active: boolean
  status: UserStatus
  must_change_password: boolean
  created_at: string
  updated_at: string
  password_changed_at: string | null
  last_login_at: string | null
  invited_at: string | null
  // Konto zaproszone: do kiedy ważny najnowszy link (w przeszłości = wygasł).
  // Tylko w odpowiedziach /api/admin.
  invite_expires_at?: string | null
}

/** Jak przedstawiać konto: e-mail, a stare konto bez adresu — loginem. */
export const accountLabel = (u: Pick<AppUser, 'login' | 'email'>): string => u.email ?? u.login

export interface RoleDef {
  key: Role
  label: string
  description: string
  sort: number
  // Systemowa (admin): ma wszystko z definicji, bez kolumny w macierzy, bez edycji.
  system: boolean
  user_count: number
}

export interface SectionDef {
  key: string
  group: string
  label: string
  hint: string
}

export interface Me {
  user: AppUser
  // Sekcje z jakimkolwiek poziomem, w kolejności katalogu (admin = wszystkie).
  sections: SectionDef[]
  // sekcja → najwyższy poziom z ról konta.
  access: Record<string, Level>
  // klucz → nazwa ról tego konta.
  role_labels: Record<Role, string>
}

/** rola nie-systemowa → {sekcja: poziom}; brak klucza sekcji = brak dostępu. */
export type AccessMatrixData = Record<Role, Record<string, Level>>

export interface AccessConfig {
  // Wszystkie role, także systemowy admin (filtrować po `system`).
  roles: RoleDef[]
  sections: SectionDef[]
  matrix: AccessMatrixData
}

/** Zaproszenie / ponowne zaproszenie / reset hasła przez admina. Link wraca RAZ
 *  (w bazie jest tylko skrót tokenu) — „Kopiuj link" na wypadek maila w spamie. */
export interface LinkResult {
  user: AppUser
  link: string
  expires_at: string
  // false = maile wyłączone (brak SMTP na serwerze) albo konto bez adresu —
  // link trzeba przekazać samemu.
  mail_queued: boolean
}

/** Cel linku z maila. */
export type TokenPurpose = 'invite' | 'reset'
/** Czemu link nie działa (ciało błędu 400/410 z /api/auth/token i /set-password). */
export type TokenProblem = 'invalid' | 'used' | 'expired' | 'disabled'

export interface TokenInfo {
  purpose: TokenPurpose
  login: string
  email: string | null
  display_name: string
  expires_at: string
}

/** Token z fragmentu adresu `/konto/ustaw-haslo#t=…` (po „#", więc nie trafia
 *  do logów serwera ani nagłówka Referer). */
export function tokenFromHash(hash: string): string | null {
  const t = new URLSearchParams(hash.replace(/^#/, '')).get('t')
  return t && t.trim() ? t.trim() : null
}

/** Nowe hasło gotowe do wysłania: długość + powtórzenie. */
export const passwordReady = (next: string, repeat: string) => next.length >= PASSWORD_MIN_LEN && next === repeat

export interface RoleCreateInput {
  label: string
  description?: string
  // Bez klucza backend robi go z nazwy („Trener bramkarzy" → trener_bramkarzy).
  key?: string
  // Rola, której dostępy (z poziomami) dostanie nowa.
  copy_from?: Role | null
}

export class AuthApiError extends Error {
  status: number
  // Tylko błędy linków z maili (400/410): powód i cel linku — po nich strona
  // ustawiania hasła wybiera wyjście („poproś admina" vs „wyślij jeszcze raz").
  reason: TokenProblem | null
  purpose: TokenPurpose | null
  constructor(status: number, message: string, reason: TokenProblem | null = null, purpose: TokenPurpose | null = null) {
    super(message)
    this.name = 'AuthApiError'
    this.status = status
    this.reason = reason
    this.purpose = purpose
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`
    let reason: TokenProblem | null = null
    let purpose: TokenPurpose | null = null
    try {
      const body = await res.json()
      if (typeof body?.detail === 'string') detail = body.detail
      // 422 z Pydantica: lista błędów pól — wystarczy pierwszy.
      else if (Array.isArray(body?.detail) && body.detail[0]?.msg) detail = body.detail[0].msg
      if (typeof body?.reason === 'string') reason = body.reason as TokenProblem
      if (body?.purpose === 'invite' || body?.purpose === 'reset') purpose = body.purpose
    } catch { /* odpowiedź bez ciała JSON */ }
    throw new AuthApiError(res.status, detail, reason, purpose)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })

export const authApi = {
  // null = niezalogowany (401), a nie błąd.
  me: () =>
    request<Me>('/api/auth/me').catch((e) => {
      if (e instanceof AuthApiError && e.status === 401) return null
      throw e
    }),
  // `login` = login albo e-mail.
  login: (login: string, password: string) => request<Me>('/api/auth/login', json('POST', { login, password })),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/auth/password', json('POST', { current_password: currentPassword, new_password: newPassword })),
  // „Nie pamiętasz hasła?" — zawsze ta sama odpowiedź (204), czy konto istnieje, czy nie.
  forgot: (email: string) => request<void>('/api/auth/forgot', json('POST', { email })),
  // Link z maila: dla kogo i do czego; błąd = AuthApiError z `reason` / `purpose`.
  checkToken: (token: string) => request<TokenInfo>('/api/auth/token', json('POST', { token })),
  // Hasło z linku → od razu zalogowany (ciasteczko sesji).
  setPassword: (token: string, password: string) =>
    request<Me>('/api/auth/set-password', json('POST', { token, password })),

  users: () => request<AppUser[]>('/api/admin/users'),
  // Zaproszenie: e-mail = login; konto bez hasła, link ważny 7 dni mailem.
  createUser: (input: { email: string; display_name: string; roles: Role[] }) =>
    request<LinkResult>('/api/admin/users', json('POST', input)),
  // email: null albo '' = usuń adres (nie dla konta z loginem = e-mail); pominięty = bez zmian.
  updateUser: (id: string, patch: Partial<Pick<AppUser, 'display_name' | 'roles' | 'active' | 'email'>>) =>
    request<AppUser>(`/api/admin/users/${id}`, json('PATCH', patch)),
  // Ponowne zaproszenie (konto zaproszone) — nowy link, poprzedni przestaje działać.
  invite: (id: string) => request<LinkResult>(`/api/admin/users/${id}/invite`, { method: 'POST' }),
  // Link do nowego hasła mailem (konto aktywne); obecne hasło działa do czasu zmiany.
  resetPassword: (id: string) => request<LinkResult>(`/api/admin/users/${id}/reset-password`, { method: 'POST' }),
  deleteUser: (id: string) => request<void>(`/api/admin/users/${id}`, { method: 'DELETE' }),

  roles: () => request<RoleDef[]>('/api/admin/roles'),
  createRole: (input: RoleCreateInput) => request<RoleDef>('/api/admin/roles', json('POST', input)),
  updateRole: (key: Role, patch: Partial<Pick<RoleDef, 'label' | 'description' | 'sort'>>) =>
    request<RoleDef>(`/api/admin/roles/${encodeURIComponent(key)}`, json('PATCH', patch)),
  // Odbiera rolę wszystkim kontom i kasuje jej dostępy.
  deleteRole: (key: Role) => request<void>(`/api/admin/roles/${encodeURIComponent(key)}`, { method: 'DELETE' }),

  access: () => request<AccessConfig>('/api/admin/access'),
  // Podmienia wiersze PODANYCH ról; role spoza `matrix` zostają bez zmian.
  saveAccess: (matrix: AccessMatrixData) => request<AccessConfig>('/api/admin/access', json('PUT', { matrix })),
}

export const isAdmin = (me: Me | null) => !!me?.user.roles.includes(ADMIN_ROLE)

/** Czy konto ma w sekcji co najmniej `level` (Edycja obejmuje Podgląd). */
export function hasAccess(me: Me | null, section: string, level: Level = 'view'): boolean {
  const have = me?.access[section]
  if (!have) return false
  return LEVELS.indexOf(have) >= LEVELS.indexOf(level)
}

/** klucz → nazwa w kolejności listy ról z API (kolejność kluczy obiektu = kolejność wstawiania). */
export const roleLabels = (roles: Pick<RoleDef, 'key' | 'label'>[]): Record<Role, string> =>
  Object.fromEntries(roles.map((r) => [r.key, r.label]))

/** „1 konto", „3 konta", „5 kont", „12 kont". */
export function accountsLabel(n: number): string {
  if (n === 1) return '1 konto'
  const few = n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14)
  return `${n} ${few ? 'konta' : 'kont'}`
}

/** Sekcje pogrupowane po `group`, w kolejności katalogu z backendu. */
export function groupSections(sections: SectionDef[]): { group: string; items: SectionDef[] }[] {
  const out: { group: string; items: SectionDef[] }[] = []
  for (const s of sections) {
    const last = out[out.length - 1]
    if (last && last.group === s.group) last.items.push(s)
    else out.push({ group: s.group, items: [s] })
  }
  return out
}

export interface MeState {
  me: Me | null
  loading: boolean
  error: string | null
  setMe: (me: Me | null) => void
  reload: () => void
}

/** Bieżąca sesja — `me` null = niezalogowany. */
export function useMe(): MeState {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    authApi.me().then(
      (m) => { if (alive) { setMe(m); setError(null); setLoading(false) } },
      (e: Error) => { if (alive) { setError(e.message); setLoading(false) } },
    )
    return () => { alive = false }
  }, [tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { me, loading, error, setMe, reload }
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** „21.10" — krótko, do statusu zaproszenia w tabeli kont. */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })
}
